import MasterCrudPage from "../../components/MasterCrudPage";
import {
  assetInventoryApi,
  assetSourceLocationApi,
  serviceApi,
  userMasterApi,
  userRoleApi,
} from "../../api/masterApi";
import {
  filterSellerUsers,
  userDisplayName,
} from "../../utils/userRoleFilters";

export default function AssetInventory() {
  return (
    <MasterCrudPage
      title="Asset Inventory"
      description="Track inventory for assets by user, source location, pricing and discounts."
      idKey="asset_inventory_id"
      api={assetInventoryApi}
      listLabel="asset inventory records"
      useJson
      scopeToCurrentUser
      loadExtraOptions={async () => {
        const [users, roles, assets, sourceLocations] = await Promise.all([
          userMasterApi.list(),
          userRoleApi.list(),
          serviceApi.list(),
          assetSourceLocationApi.list(),
        ]);

        const sellers = filterSellerUsers(users, roles);

        return {
          users: sellers.map((user) => ({
            value: user.user_id,
            label: userDisplayName(user),
          })),
          assets: (assets || []).map((asset) => ({
            value: asset.service_id,
            label: asset.service_title || "Asset",
          })),
          sourceLocations: (sourceLocations || []).map((location) => ({
            value: location.asset_source_location_id,
            label: location.asset_source_location,
          })),
        };
      }}
      fields={[
        {
          name: "user_id",
          label: "Seller User",
          type: "select",
          required: true,
          optionsKey: "users",
          placeholder: "Select seller",
        },
        {
          name: "asset_id",
          label: "Asset",
          type: "select",
          required: true,
          optionsKey: "assets",
          placeholder: "Select asset",
        },
        {
          name: "asset_source_location_id",
          label: "Asset Source Location",
          type: "select",
          optionsKey: "sourceLocations",
          placeholder: "Select source location",
        },
        {
          name: "inventory_size",
          label: "Inventory Size",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "Enter inventory size",
        },
        {
          name: "inventory_datetime",
          label: "Date and Time",
          type: "datetime-local",
        },
        {
          name: "inventory_location",
          label: "Inventory Location",
          type: "text",
          fullWidth: true,
          placeholder: "Enter inventory location",
        },
        {
          name: "unit_price_inr",
          label: "Unit Price in INR",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "unit_price_usd",
          label: "Unit Price in USD",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "discount_inr",
          label: "Discount in INR",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "discount_usd",
          label: "Discount in USD",
          type: "number",
          step: "0.01",
          min: "0",
          placeholder: "0.00",
        },
        {
          name: "discount_valid_from",
          label: "Discount Valid From",
          type: "date",
        },
        {
          name: "discount_valid_upto",
          label: "Discount Valid Upto",
          type: "date",
        },
        {
          name: "comments",
          label: "Comments",
          type: "textarea",
          fullWidth: true,
          placeholder: "Enter comments",
        },
      ]}
      columns={[
        { key: "asset_inventory_id", label: "#" },
        {
          key: "user_id",
          label: "Seller",
          render: (item) =>
            [item.first_name, item.last_name].filter(Boolean).join(" ") || "—",
        },
        {
          key: "asset_id",
          label: "Asset",
          render: (item) => item.service_title || "—",
        },
        {
          key: "asset_source_location_id",
          label: "Asset Source Location",
          render: (item) => item.asset_source_location || "—",
        },
        { key: "inventory_size", label: "Inventory Size" },
        {
          key: "inventory_datetime",
          label: "Date and Time",
          render: (item) =>
            item.inventory_datetime
              ? String(item.inventory_datetime).replace("T", " ").slice(0, 16)
              : "—",
        },
        { key: "inventory_location", label: "Inventory Location" },
        { key: "unit_price_inr", label: "Unit Price (INR)" },
        { key: "unit_price_usd", label: "Unit Price (USD)" },
        { key: "discount_inr", label: "Discount (INR)" },
        { key: "discount_usd", label: "Discount (USD)" },
        { key: "discount_valid_from", label: "Discount From" },
        { key: "discount_valid_upto", label: "Discount Upto" },
        { key: "comments", label: "Comments" },
      ]}
    />
  );
}
